/**
 * NAMED FAILURE STATES 2 AND 3 — AN EXPIRED CODE AND A CODE USED TWICE. ONE PANEL, BECAUSE THEY
 * ARE ONE ANSWER.
 *
 * THIS IS THE MOST IMPORTANT COMMENT IN THIS DIRECTORY. Unknown, expired and already-used are
 * deliberately indistinguishable: "Redemption is one conditional UPDATE and single use is therefore
 * a database fact rather than an application convention. Unknown, expired and already-used are
 * deliberately indistinguishable — zero rows covers all three and the response says so with one
 * answer, so the spec registers one refusal and not three"
 * (docs/design/07-api/authors-api.yaml). The refusal is 401 / `HANDOFF_CODE_INVALID` and
 * "nothing in the body distinguishes them". Milestone A1's acceptance script requires all three to
 * be indistinguishable (docs/design/12-build-plan.md).
 *
 * SO THERE IS ONE MESSAGE AND THERE MUST NOT BE THREE. A screen that says "this link has already
 * been used" is guessing, and it will guess wrong for the person whose link simply expired — who
 * will then think somebody else has their account. The copy is in ../copy.ts as a single constant
 * for exactly that reason.
 *
 * AND IT DOES NOT OFFER TO RETRY THE SAME CODE. The code is dead in all three cases: a second
 * exchange cannot succeed. It offers the two doors that do work — a fresh link from the app, or an
 * ordinary sign-in.
 *
 * NOBODY IS SIGNED OUT HERE. This 401 is about the CODE, not about the caller: the operation
 * carries `security: []` and there is no session for it to have rejected.
 */
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from '../../../i18n';
import { HANDOFF_CODE_DEAD_BODY, HANDOFF_CODE_DEAD_TITLE } from '../copy';

export interface HandoffCodeDeadPanelProps {
  readonly onSignInInstead: () => void;
}

export function HandoffCodeDeadPanel({ onSignInInstead }: HandoffCodeDeadPanelProps) {
  const { t } = useTranslation('auth');
  return (
    <Alert role="alert" variant="light" color="yellow" title={t(HANDOFF_CODE_DEAD_TITLE)}>
      <Stack gap="sm">
        <Text size="sm">{t(HANDOFF_CODE_DEAD_BODY)}</Text>
        <Text size="sm" c="dimmed">
          {t('handoffCodeDead.notLockedOut')}
        </Text>
        <Group gap="sm">
          <Button variant="filled" size="sm" onClick={onSignInInstead}>
            {t('common.signInHereInstead')}
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
}
